// frontend/src/pages/PayCycle.tsx
// The one-page monthly close: both books' pending payouts, one "Confirm &
// download" button that pays + closes both runs and downloads a single
// Comissio CSV. System-agnostic admin route (/cycle).
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  CircleDollarSign,
  CheckCircle2,
  AlertCircle,
  Circle,
  Download,
  RefreshCw,
  TrendingDown,
  XCircle,
} from 'lucide-react';
import {
  api,
  fmt,
  type System,
  type PaySummaryAgent,
  type PayRun,
  type Payment,
  type CycleBookResult,
  type CycleConfirmResult,
} from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmModal from '../components/ConfirmModal';
import QueuedAgentsTable from '../components/QueuedAgentsTable';
import BookBadge, { bookColor } from '../components/BookBadge';
import { computeChecklist, type ChecklistItem } from '../components/ImportChecklist';
import { useToast } from '../contexts/ToastContext';

const BOOKS: System[] = ['Messer', 'Empire-ACA'];
const slugFor = (s: System) => (s === 'Messer' ? 'messer' : 'empire-aca');

type BookView = {
  sys: System;
  openRun: PayRun | null;
  lastClosed: PayRun | null;
  payable: PaySummaryAgent[];
  held: PaySummaryAgent[];
  unpaidNet: number;
  houseTotal: number;
  unattributedRows: number;
  checklist: ChecklistItem[];
};

export default function PayCycle() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CycleConfirmResult | null>(null);

  // 4 queries × 2 books. useQueries keeps the hook count constant.
  const queries = useQueries({
    queries: BOOKS.flatMap((sys) => [
      { queryKey: ['pay-summary', sys], queryFn: () => api.paySummary(sys) },
      { queryKey: ['pay-runs', sys], queryFn: () => api.listPayRuns(sys) },
      { queryKey: ['carriers', sys], queryFn: () => api.listCarriers(sys) },
      { queryKey: ['batches', sys], queryFn: () => api.listBatches(sys) },
    ]),
  });
  const loading = queries.some((q) => q.isLoading);
  const hardError = queries.find((q) => q.isError && !q.data)?.error as Error | undefined;
  const hasSoftError = queries.some((q) => q.isError && q.data);

  const books: BookView[] = useMemo(() => {
    return BOOKS.map((sys, i) => {
      const summary = queries[i * 4].data as Awaited<ReturnType<typeof api.paySummary>> | undefined;
      const runs = (queries[i * 4 + 1].data as { pay_runs: PayRun[] } | undefined)?.pay_runs ?? [];
      const carriers = (queries[i * 4 + 2].data as Awaited<ReturnType<typeof api.listCarriers>> | undefined)?.carriers ?? [];
      const batches = (queries[i * 4 + 3].data as Awaited<ReturnType<typeof api.listBatches>> | undefined)?.batches ?? [];
      const openRun = runs.find((r) => r.status === 'open') ?? null;
      const lastClosed =
        runs
          .filter((r) => r.status === 'closed')
          .sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? ''))[0] ?? null;
      return {
        sys,
        openRun,
        lastClosed,
        payable: summary?.agents ?? [],
        held: summary?.skipped_negative_balance ?? [],
        unpaidNet: summary?.totals.unpaid_net ?? 0,
        houseTotal: summary?.totals.house_total ?? 0,
        unattributedRows: summary?.totals.unattributed_rows ?? 0,
        checklist: computeChecklist(carriers, batches, runs).items,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((q) => q.dataUpdatedAt).join(',')]);

  // "Paid this cycle" detail: the payments behind the runs this page is
  // reporting on — the runs just closed by Confirm, else each book's last
  // closed run. Hook count stays constant (useQueries), array length varies.
  const detailRunIds: string[] = result?.closed_pay_run_ids?.length
    ? result.closed_pay_run_ids
    : books.map((b) => b.lastClosed?.pay_run_id).filter((id): id is string => !!id);
  const paymentQueries = useQueries({
    queries: detailRunIds.map((id) => ({
      queryKey: ['pay-run', id],
      queryFn: () => api.getPayRun(id),
    })),
  });
  const paidRuns: PaidRun[] = paymentQueries
    .map((q) => q.data)
    .filter((d): d is NonNullable<typeof d> => !!d)
    .map((d) => ({ run: d.pay_run, payments: d.payments }));
  const paidLoading = paymentQueries.some((q) => q.isLoading);

  if (loading) return <LoadingSpinner label="Loading both books…" />;
  if (hardError)
    return (
      <div className="alert alert-error flex items-center justify-between gap-3">
        <p>{hardError.message}</p>
        <button
          className="btn btn-secondary text-sm shrink-0"
          onClick={() => queries.forEach((q) => q.refetch())}
        >
          Retry
        </button>
      </div>
    );

  const openBooks = books.filter((b) => b.openRun);
  const grandTotal = openBooks.reduce((s, b) => s + b.unpaidNet, 0);
  const agentCount = openBooks.reduce((s, b) => s + b.payable.length, 0);
  const labels = [...new Set(openBooks.map((b) => b.openRun!.period_label))];
  const periodLabel = labels.length === 1 ? labels[0] : null;
  const nothingOpen = openBooks.length === 0;

  const warnings: { key: string; text: ReactNode }[] = [];
  if (openBooks.length === 2 && !periodLabel) {
    warnings.push({
      key: 'period',
      text: `The two open runs are labelled differently (${labels.join(' vs ')}). Confirm still closes both; rename one on its Pay Runs page first if that is wrong.`,
    });
  }
  for (const b of openBooks) {
    if (b.unattributedRows > 0) {
      warnings.push({
        key: `unattr-${b.sys}`,
        text: (
          <>
            <BookBadge sys={b.sys} /> {b.unattributedRows.toLocaleString()} rows are unattributed and will not be
            paid — map their writing IDs in{' '}
            <Link to={`/${slugFor(b.sys)}/carriers`} style={{ color: 'var(--color-primary)' }}>
              Carriers
            </Link>
            .
          </>
        ),
      });
    }
    const waiting = b.checklist.filter((c) => c.status === 'waiting');
    if (waiting.length > 0) {
      warnings.push({
        key: `waiting-${b.sys}`,
        text: (
          <>
            <BookBadge sys={b.sys} /> still waiting on {waiting.map((w) => w.displayName).join(', ')}. Their rows
            roll into the next cycle if you confirm now.
          </>
        ),
      });
    }
  }

  const invalidate = () => {
    for (const sys of BOOKS) {
      queryClient.invalidateQueries({ queryKey: ['pay-summary', sys] });
      queryClient.invalidateQueries({ queryKey: ['pay-runs', sys] });
      queryClient.invalidateQueries({ queryKey: ['batches', sys] });
    }
    queryClient.invalidateQueries({ queryKey: ['payments'] });
  };

  const csvName = (label: string | null) =>
    label ? `payments-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv` : `payments-${new Date().toISOString().slice(0, 10)}.csv`;

  const runConfirm = async (systems?: System[]) => {
    if (busy) return;
    const expected: Partial<Record<System, { pay_run_id: string; unpaid_net: number }>> = {};
    for (const b of openBooks) {
      if (systems && !systems.includes(b.sys)) continue;
      expected[b.sys] = { pay_run_id: b.openRun!.pay_run_id, unpaid_net: b.unpaidNet };
    }
    setBusy(true);
    setConfirming(false);
    try {
      const r = await api.confirmCycle(expected, systems);
      const merged: CycleConfirmResult =
        result && systems
          ? {
              ...r,
              results: { ...result.results, ...r.results },
              closed_pay_run_ids: [...new Set([...result.closed_pay_run_ids, ...r.closed_pay_run_ids])],
            }
          : r;
      setResult(merged);
      const closed = Object.values(merged.results).filter((x) => x?.status === 'closed').length;
      if (closed > 0) toast.success(`Closed ${closed} run${closed === 1 ? '' : 's'}`);
      if (merged.closed_pay_run_ids.length > 0) {
        try {
          await api.downloadCyclePaymentsCsv(merged.closed_pay_run_ids, csvName(merged.period_label ?? periodLabel));
        } catch (err) {
          toast.error(`CSV download failed — use "Download CSV again": ${(err as Error).message}`);
        }
      }
      invalidate();
    } catch (err) {
      invalidate();
      toast.error(
        `Confirm request failed (${(err as Error).message}). The runs may still have closed — this page has refreshed; review it before retrying.`,
      );
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  // ---- Closed-cycle view: nothing open in either book ----
  if (nothingOpen && !result) {
    const closedRuns = books.map((b) => b.lastClosed).filter(Boolean) as PayRun[];
    const closedIds = closedRuns.map((r) => r.pay_run_id);
    const closedLabels = [...new Set(closedRuns.map((r) => r.period_label))];
    const closedLabel = closedLabels.length === 1 ? closedLabels[0] : null;
    const closedTotal = closedRuns.reduce((s, r) => s + (r.total ?? 0), 0);
    return (
      <div className="space-y-6 animate-fadeIn">
        {hasSoftError && (
          <div className="alert alert-warning text-sm">Some data failed to refresh — showing last known values.</div>
        )}
        <PageTitle subtitle="No open pay run in either book. The next cycle opens itself when the first statement lands." />
        <div className="card" style={{ borderLeft: '4px solid var(--color-primary)' }}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Last closed cycle{closedLabel ? ` · ${closedLabel}` : ''}
              </p>
              <div className="mt-2 space-y-1">
                {books.map((b) =>
                  b.lastClosed ? (
                    <p key={b.sys} className="text-sm flex items-center gap-2">
                      <BookBadge sys={b.sys} />
                      <span style={{ color: 'var(--color-text)' }}>{b.lastClosed.name}</span>
                      <span className="font-mono font-semibold">{fmt.money(b.lastClosed.total)}</span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {b.lastClosed.agent_count} agents · closed {fmt.date(b.lastClosed.closed_at)}
                      </span>
                    </p>
                  ) : null,
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Paid
              </p>
              <p className="text-2xl font-semibold font-mono" style={{ color: 'var(--color-text)' }}>
                {fmt.money(closedTotal)}
              </p>
              <button
                className="btn btn-secondary text-sm mt-2 flex items-center gap-2"
                disabled={closedIds.length === 0}
                onClick={() =>
                  api
                    .downloadCyclePaymentsCsv(closedIds, csvName(closedLabel))
                    .catch((err) => toast.error(`CSV download failed: ${(err as Error).message}`))
                }
              >
                <Download size={14} /> Download CSV again
              </button>
            </div>
          </div>
        </div>
        <PaidTable runs={paidRuns} loading={paidLoading} />
      </div>
    );
  }

  // ---- Open-cycle view (and post-confirm results) ----
  // Don't latch "done" when a confirm attempt closed nothing (e.g. every
  // book came back stale) — keep the Confirm button available for a full
  // retry. Per-book ResultLines are gated on `result` directly, not `done`.
  const done = !!result && result.closed_pay_run_ids.length > 0;
  return (
    <div className="space-y-6 animate-fadeIn">
      {hasSoftError && (
        <div className="alert alert-warning text-sm">Some data failed to refresh — showing last known values.</div>
      )}
      <PageTitle
        label={periodLabel ?? undefined}
        subtitle={
          done
            ? 'Cycle confirmed. Results per book below.'
            : `${agentCount} agent${agentCount === 1 ? '' : 's'} across ${openBooks.length} book${openBooks.length === 1 ? '' : 's'} · pays everyone queued, closes ${openBooks.length === 1 ? 'the run' : 'both runs'}, downloads one Comissio CSV.`
        }
      />

      {/* Header card: grand total + the button */}
      <div className="card" style={{ borderLeft: '4px solid var(--color-primary)' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
              {done ? 'Paid this cycle' : 'Total to pay'}
            </p>
            <p className="text-3xl font-semibold font-mono" style={{ color: 'var(--color-text)' }}>
              {fmt.money(
                done
                  ? Object.values(result!.results).reduce((s, r) => s + (r?.status === 'closed' ? r.total ?? 0 : 0), 0)
                  : grandTotal,
              )}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {openBooks.map((b) => (
                <span key={b.sys} className="flex items-center gap-1">
                  <BookBadge sys={b.sys} /> {fmt.money(b.unpaidNet)}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {done && result!.closed_pay_run_ids.length > 0 && (
              <button
                className="btn btn-secondary text-sm flex items-center gap-2"
                onClick={() =>
                  api
                    .downloadCyclePaymentsCsv(result!.closed_pay_run_ids, csvName(result!.period_label ?? periodLabel))
                    .catch((err) => toast.error(`CSV download failed: ${(err as Error).message}`))
                }
              >
                <Download size={14} /> Download CSV again
              </button>
            )}
            {!done && (
              <button
                className="btn btn-primary text-base px-5 py-2.5 flex items-center gap-2 disabled:opacity-40"
                disabled={busy || openBooks.length === 0}
                onClick={() => setConfirming(true)}
              >
                <CheckCircle2 size={16} /> {busy ? 'Working…' : 'Confirm & download'}
              </button>
            )}
          </div>
        </div>
        {warnings.length > 0 && !done && (
          <div className="mt-4 space-y-2">
            {warnings.map((w) => (
              <div key={w.key} className="alert alert-warning flex items-start gap-2 text-sm">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <p>{w.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* After a confirm: the payments that just went out, click-through to policies */}
      {done && <PaidTable runs={paidRuns} loading={paidLoading} />}

      {/* One card per book, fixed order */}
      {books.map((b) => (
        <BookCard
          key={b.sys}
          book={b}
          result={result?.results[b.sys]}
          busy={busy}
          onRetry={() => runConfirm([b.sys])}
          onRefresh={() => {
            setResult(null);
            invalidate();
          }}
        />
      ))}

      <ConfirmModal
        open={confirming}
        title={`Confirm ${periodLabel ?? 'this cycle'}?`}
        message={
          openBooks.map((b) => `${b.sys}: ${b.openRun!.name} — ${b.payable.length} agents, ${fmt.money(b.unpaidNet)}`).join('\n') +
          `\n\nTotal ${fmt.money(grandTotal)}. This pays every queued agent, closes ${openBooks.length === 1 ? 'the run' : 'both runs'}, emails agents their statements, and downloads one CSV. It cannot be undone.`
        }
        confirmLabel={busy ? 'Working…' : 'Confirm & download'}
        variant="warning"
        onConfirm={() => runConfirm()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

type PaidRun = { run: PayRun; payments: Payment[] };

/**
 * "Paid this cycle": one row per Payment across the cycle's closed runs —
 * the same rows the Comissio CSV carries, grouped Messer → Empire-ACA with a
 * subtotal per book. Click a row to open the payment's policy-level detail.
 */
function PaidTable({ runs, loading }: { runs: PaidRun[]; loading: boolean }) {
  const navigate = useNavigate();
  if (loading && runs.length === 0) return <LoadingSpinner label="Loading payments…" />;
  if (runs.length === 0) return null;

  const groups = BOOKS.map((sys) => {
    const forBook = runs.filter((r) => r.run.sys === sys);
    const payments = forBook
      .flatMap((r) => r.payments)
      .slice()
      .sort((a, b) => (b.total || 0) - (a.total || 0));
    return { sys, runs: forBook, payments, subtotal: payments.reduce((s, p) => s + (p.total || 0), 0) };
  }).filter((g) => g.runs.length > 0);
  const grand = groups.reduce((s, g) => s + g.subtotal, 0);
  const count = groups.reduce((s, g) => s + g.payments.length, 0);

  return (
    <div className="card overflow-hidden p-0">
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          Paid this cycle ({count} payment{count === 1 ? '' : 's'} · {fmt.money(grand)})
        </h3>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Same rows as the CSV. Click a payment to see the policies behind it.
        </p>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 90 }}>Book</th>
            <th>Agent</th>
            <th className="text-right">Rows</th>
            <th>Per-carrier breakdown</th>
            <th className="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <GroupRows key={g.sys} group={g} onOpen={(p) => navigate(`/${slugFor(g.sys)}/payments/${p.payment_id}`)} />
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--color-border)' }}>
            <td colSpan={4} className="text-right text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              Total
            </td>
            <td className="text-right font-mono font-semibold text-base">{fmt.money(grand)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function GroupRows({
  group,
  onOpen,
}: {
  group: { sys: System; payments: Payment[]; subtotal: number };
  onOpen: (p: Payment) => void;
}) {
  return (
    <>
      {group.payments.map((p) => {
        const held = p.held || (p.total || 0) === 0;
        return (
          <tr
            key={p.payment_id}
            onClick={() => onOpen(p)}
            className="cursor-pointer"
            title="Open payment detail (policies)"
          >
            <td>
              <BookBadge sys={group.sys} />
            </td>
            <td className="font-medium">
              {p.agent_name}
              {held && (
                <span className="badge badge-warning text-[10px] ml-2" title="Net ≤ $0 after carry-forward — $0 disbursed, balance carried">
                  held
                </span>
              )}
            </td>
            <td className="text-right font-mono">{p.row_count}</td>
            <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {Object.entries(p.by_carrier || {})
                .map(([c, v]) => `${c}: ${v.rows} (${fmt.money(v.net)})`)
                .join(' · ')}
            </td>
            <td className="text-right font-mono font-semibold">{fmt.money(p.total)}</td>
          </tr>
        );
      })}
      <tr style={{ background: 'var(--color-section)' }}>
        <td colSpan={4} className="text-right text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--color-text-muted)' }}>
          {group.sys} subtotal
        </td>
        <td className="text-right font-mono font-semibold">{fmt.money(group.subtotal)}</td>
      </tr>
    </>
  );
}

function PageTitle({ label, subtitle }: { label?: string; subtitle: string }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
        <CircleDollarSign size={20} className="inline mr-2" style={{ color: 'var(--color-primary)' }} />
        Pay Cycle{label ? ` — ${label}` : ''}
      </h2>
      <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
        {subtitle}
      </p>
    </div>
  );
}

function ChecklistChips({ items }: { items: ChecklistItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 text-xs">
      {items.map((c) => (
        <span
          key={c.carrier}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          title={c.detail}
          style={{
            background:
              c.status === 'loaded'
                ? 'rgba(34, 197, 94, 0.12)'
                : c.status === 'attention'
                  ? 'rgba(245, 158, 11, 0.14)'
                  : 'transparent',
            border: c.status === 'waiting' ? '1px dashed var(--color-border)' : '1px solid transparent',
            color: c.status === 'waiting' ? 'var(--color-text-muted)' : 'var(--color-text)',
          }}
        >
          {c.status === 'loaded' ? (
            <CheckCircle2 size={11} className="text-green-500" />
          ) : c.status === 'attention' ? (
            <AlertCircle size={11} className="text-amber-500" />
          ) : (
            <Circle size={11} />
          )}
          {c.displayName}
          {c.status === 'waiting' && <span>· waiting</span>}
        </span>
      ))}
    </div>
  );
}

function ResultLine({ r, sys, onRefresh }: { r: CycleBookResult; sys: System; onRefresh: () => void }) {
  if (r.status === 'closed')
    return (
      <p className="text-sm flex items-center gap-2 text-green-600 dark:text-green-300">
        <CheckCircle2 size={14} /> Paid {r.paid_agents ?? 0} agent{(r.paid_agents ?? 0) === 1 ? '' : 's'} ·{' '}
        {fmt.money(r.total ?? 0)} · closed
        {r.already_closed ? ' (was already closed)' : ` · ${r.emails_sent ?? 0} email${(r.emails_sent ?? 0) === 1 ? '' : 's'} sent`}
        {(r.held_agents ?? 0) > 0 ? ` · ${r.held_agents} held` : ''}
      </p>
    );
  if (r.status === 'stale')
    return (
      <>
        <p className="text-sm flex items-center gap-2 text-amber-600 dark:text-amber-300">
          <AlertCircle size={14} /> Numbers changed since you loaded this page (now {fmt.money(r.live_unpaid_net ?? 0)}) — nothing
          was paid for {sys}. Refresh and review again.
        </p>
        <button className="btn btn-secondary text-xs flex items-center gap-1" onClick={onRefresh}>
          <RefreshCw size={12} /> Refresh & review
        </button>
      </>
    );
  if (r.status === 'error')
    return (
      <p className="text-sm flex items-center gap-2 text-red-600 dark:text-red-300">
        <XCircle size={14} /> {r.error}
        {r.paid_agents !== undefined ? ` (payments were created; the run is still open)` : ''}
      </p>
    );
  return (
    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
      {r.status === 'no_open_run' ? 'No open run — nothing to do.' : 'Skipped.'}
    </p>
  );
}

function BookCard({
  book,
  result,
  busy,
  onRetry,
  onRefresh,
}: {
  book: BookView;
  result?: CycleBookResult;
  busy: boolean;
  onRetry: () => void;
  onRefresh: () => void;
}) {
  const color = bookColor(book.sys);
  const slug = slugFor(book.sys);

  if (!book.openRun && !result) {
    return (
      <div className="card" style={{ borderLeft: `6px solid ${color}`, opacity: 0.7 }}>
        <div className="flex items-center gap-3 flex-wrap">
          <BookBadge sys={book.sys} size="md" />
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            No open run — nothing to pay this cycle.
          </span>
          {book.lastClosed && (
            <span className="text-xs ml-auto" style={{ color: 'var(--color-text-muted)' }}>
              last: {book.lastClosed.name} · {fmt.money(book.lastClosed.total)}
            </span>
          )}
        </div>
      </div>
    );
  }

  const run = book.openRun ?? book.lastClosed;

  return (
    <div className="card overflow-hidden p-0" style={{ borderLeft: `6px solid ${color}` }}>
      <div className="px-4 py-3 space-y-2" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <BookBadge sys={book.sys} size="md" />
            {!book.openRun && result && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(148,163,184,0.15)', color: 'var(--color-text-muted)' }}
              >
                closed
              </span>
            )}
            <div>
              <p className="font-semibold" style={{ color: 'var(--color-text)' }}>
                {run?.name ?? book.sys}
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {run?.period_label ? `${run.period_label} · ` : ''}
                {run?.created_at ? `started ${fmt.date(run.created_at)}` : ''}
                {book.houseTotal > 0 ? ` · house keeps ${fmt.money(book.houseTotal)}` : ''}
              </p>
            </div>
          </div>
          {book.openRun && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                  Pending
                </p>
                <p className="text-lg font-semibold font-mono" style={{ color }}>
                  {fmt.money(book.unpaidNet)}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {book.payable.length} agent{book.payable.length === 1 ? '' : 's'}
                </p>
              </div>
              <Link
                to={`/${slug}/pay-runs/${book.openRun.pay_run_id}`}
                className="btn btn-secondary text-xs"
                title="Open this run's detail page"
              >
                Open run →
              </Link>
            </div>
          )}
        </div>
        <ChecklistChips items={book.checklist} />
        {result && (
          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <ResultLine r={result} sys={book.sys} onRefresh={onRefresh} />
            {result.status === 'error' && (
              <button className="btn btn-secondary text-xs flex items-center gap-1" disabled={busy} onClick={onRetry}>
                <RefreshCw size={12} /> Retry {book.sys}
              </button>
            )}
          </div>
        )}
      </div>

      {book.payable.length > 0 ? (
        <QueuedAgentsTable agents={book.payable} sys={book.sys} showBook footerLabel={`${book.sys} subtotal`} />
      ) : (
        <p className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {result ? 'Nothing pending in this book.' : 'Nothing pending in this book. Confirm will just close the run.'}
        </p>
      )}

      {book.held.length > 0 && (
        <div className="px-4 py-2 text-xs flex items-center gap-2" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
          <TrendingDown size={12} className="text-amber-500" />
          Held (carried forward): {book.held.map((a) => `${a.agent_name} ${fmt.money(a.net_payable ?? a.unpaid_net)}`).join(' · ')}
        </div>
      )}
    </div>
  );
}
